import { useState, useEffect, useCallback, useRef } from "react"

export const useElementObserver = (
  targetRef: {
    current: HTMLDivElement | null
  },
  options?: {
    intersectionOffset?: number
    threshhold?: number
  }
) => {
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [intersectionRatio, setIntersectionRatio] = useState<number>(0)
  const observerRef = useRef<IntersectionObserver>()
  const startObservingIntervalId = useRef<NodeJS.Timeout>()
  const alreadyObserving = useRef<boolean>()

  const initialiseNewObserver = useCallback(() => {
    const observerOptions = {
      root: null,
      rootMargin: `${options?.intersectionOffset || 0}px`,
      threshold: options?.threshhold || 0,
    }

    observerRef.current = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (entry.isIntersecting) {
            setIntersectionRatio(entry.intersectionRatio)
            setIsVisible(true)
          } else {
            setIntersectionRatio(0)
            setIsVisible(false)
          }
        })
      },
      observerOptions
    )
  }, [options])

  const disconnectObserver = () => {
    if (observerRef.current) {
      observerRef.current?.disconnect()
    }

    if (startObservingIntervalId.current) {
      clearInterval(startObservingIntervalId.current)
    }
  }

  const startObserving = useCallback(() => {
    if (alreadyObserving.current) {
      return
    }

    startObservingIntervalId.current = setInterval(() => {
      if (targetRef && targetRef.current && observerRef.current) {
        observerRef.current.observe(targetRef.current)
        alreadyObserving.current = true
        clearInterval(startObservingIntervalId.current)
      }
    }, 100)
  }, [observerRef, targetRef])

  useEffect(() => {
    initialiseNewObserver()

    return () => {
      disconnectObserver()
    }
  }, [initialiseNewObserver])

  useEffect(() => {
    if (alreadyObserving.current) {
      disconnectObserver()
      alreadyObserving.current = false
      initialiseNewObserver()
      startObserving()
    }
  }, [options, initialiseNewObserver, startObserving])

  return { isVisible, intersectionRatio, startObserving }
}

export const useMultipleElementsObserver = (
  targetRefs: {
    current: Map<string, HTMLDivElement> | null
  },
  options?: {
    intersectionOffset?: number
    threshhold?: number
  }
) => {
  const observerRefs = useRef<Record<string, IntersectionObserver>>({})
  const [visibleElement, setVisibleElement] = useState<HTMLDivElement>()
  const alreadyObserving = useRef<Record<string, boolean>>({})
  const startObservingIntervalIds = useRef<Record<string, NodeJS.Timeout>>({})
  const intersectionRatios = useRef<Record<string, number>>({})

  const handleIntersectionChange = () => {
    let largestRatio = ""
    for (const key in intersectionRatios.current) {
      const ratio = intersectionRatios.current[key]
      if (
        (ratio > 0 &&
          largestRatio in intersectionRatios.current &&
          ratio > intersectionRatios.current[largestRatio]) ||
        (ratio > 0 && !largestRatio)
      ) {
        largestRatio = key
      }
    }

    if (largestRatio && targetRefs.current) {
      setVisibleElement(targetRefs.current.get(largestRatio))
    } else {
      setVisibleElement(undefined)
    }
  }

  const initialiseNewObservers = useCallback(() => {
    const observerOptions = {
      root: null,
      rootMargin: `${options?.intersectionOffset || 0}px`,
      threshold: options?.threshhold || 0,
    }

    targetRefs.current?.forEach((targetRef, key) => {
      if (targetRef) {
        observerRefs.current[key] = new IntersectionObserver(
          (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
              if (entry.isIntersecting) {
                intersectionRatios.current[key] = entry.intersectionRatio
              } else {
                intersectionRatios.current[key] = 0
              }
              handleIntersectionChange()
            })
          },
          observerOptions
        )
      }
    })
  }, [options, targetRefs])

  const disconnectObservers = () => {
    for (const observer of Object.values(observerRefs.current)) {
      observer.disconnect()
    }

    for (const interval of Object.values(startObservingIntervalIds.current)) {
      clearInterval(interval)
    }
  }

  const startObserving = useCallback(() => {
    if (Object.keys(observerRefs.current).length > 0) {
      targetRefs.current?.forEach((targetRef, key) => {
        if (key in alreadyObserving.current) {
          return
        }

        startObservingIntervalIds.current[key] = setInterval(() => {
          if (targetRef && key in observerRefs.current) {
            observerRefs.current[key].observe(targetRef)
            alreadyObserving.current[key] = true
            clearInterval(startObservingIntervalIds.current[key])
          }
        }, 100)
      })
    }
  }, [observerRefs, targetRefs])

  useEffect(() => {
    initialiseNewObservers()

    return () => {
      disconnectObservers()
    }
  }, [])

  useEffect(() => {
    if (Object.keys(alreadyObserving.current).length > 0) {
      disconnectObservers()
      alreadyObserving.current = {}
      initialiseNewObservers()
      startObserving()
    }
  }, [options, initialiseNewObservers, startObserving])

  return { visibleElement, startObserving }
}
